import { RateSource } from "@redotech/money/rate-source";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { Surcharge } from "@redotech/redo-model/outbound-labels/surcharge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { BalanceTransactionBreakdownSlideout } from "../../surcharge-disputes/balance-transaction-breakdown-slideout";
import { CreateSurchargeDisputeModal } from "../../surcharge-disputes/create-surcharge-dispute-modal";

export const ShipmentAdjustmentsSection = memo(
  function ShipmentAdjustmentsSection({
    adjustments,
    fulfillmentGroupId,
  }: {
    adjustments: (Surcharge & { balanceTransactionId: string })[];
    fulfillmentGroupId: string;
  }) {
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [disputeTransactionId, setDisputeTransactionId] = useState<
      string | null
    >(null);

    return (
      <Flex dir="column" gap="sm">
        <Text fontSize="xs">Adjustments</Text>
        {adjustments.map((adjustment) => (
          <ShipmentAdjustmentRow
            adjustment={adjustment}
            key={adjustment.balanceTransactionId}
            onDispute={() =>
              setDisputeTransactionId(adjustment.balanceTransactionId)
            }
          />
        ))}
        <RedoButton
          hierarchy="tertiary"
          onClick={() => setBreakdownOpen(true)}
          size="xs"
          text="View breakdown"
        />
        <BalanceTransactionBreakdownSlideout
          fulfillmentGroupId={fulfillmentGroupId}
          isOpen={breakdownOpen}
          onClose={() => setBreakdownOpen(false)}
          onCreateDispute={(id) => {
            setDisputeTransactionId(id);
            setBreakdownOpen(false);
          }}
        />
        {disputeTransactionId && (
          <ClientUploader>
            <CreateSurchargeDisputeModal
              balanceTransactionId={disputeTransactionId}
              isOpen
              onClose={() => setDisputeTransactionId(null)}
              onCreated={() => setDisputeTransactionId(null)}
            />
          </ClientUploader>
        )}
      </Flex>
    );
  },
);

export const ShipmentAdjustmentRow = memo(function ShipmentAdjustmentRow({
  adjustment,
  onDispute,
}: {
  adjustment: Surcharge & { balanceTransactionId: string };
  onDispute: () => void;
}) {
  return (
    <Flex align="center" dir="row" gap="sm" justify="space-between">
      <ShipmentAdjustmentText adjustment={adjustment} />

      <RedoButton onClick={onDispute} size="xs" text="Dispute" />
    </Flex>
  );
});

export const ShipmentAdjustmentText = memo(function ShipmentAdjustmentText({
  adjustment,
}: {
  adjustment: Surcharge & { balanceTransactionId: string };
}) {
  return (
    <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
      {adjustment.description}
      {" - "}
      <Text as="span" fontSize="xs" textColor="error">
        <MoneyText
          source={RateSource.Stripe}
          value={toMoney({
            amount: Math.abs(Number(adjustment.amount)),
            currency: adjustment.currency,
          })}
        />
      </Text>
    </Text>
  );
});
